/**
 * DebugPavilion —— SVG+WASM 混合渲染 · WASM 几何内核的 TS 封装。
 *
 * 职责：
 *  1. 异步加载 wasm（单例 + 失败降级）：wasm 不可用时功能不死，自动回落到纯 TS 实现。
 *  2. 类型化 API：computeLayout / hitTest / snapToGuides 与 WASM 同签名。
 *  3. 纯 TS 实现（*TS 后缀）：与 wasm/geometry/assembly/index.ts 逻辑一致，
 *     jsdom 测试与降级路径共用；两者等价性由 wasm-geometry.equivalence.test.ts 保障。
 *
 * 接口契约（两侧一致，见 assembly/index.ts 顶部注释）：
 *  - computeLayout(tree) -> LayoutRect[]（pre-order DFS，绝对坐标，含 parentId/depth）
 *  - hitTest(x, y, rects) -> string | null（z-order：rects 中最后命中的矩形为最上层）
 *  - snapToGuides(moving, others, threshold) -> { dx, dy, guides }
 *
 * 加载说明：AssemblyScript 0.27 的 --bindings raw 会生成配套的 loader（geometry.js，
 * 内含字符串升降转换），本文件直接复用；wasm 字节经 fetch + WebAssembly.compile 实例化，
 * 不依赖 instantiateStreaming，对 MIME（nginx 未配置 application/wasm）不敏感。
 */

import type { ComponentNode } from "@shared/types";

import { instantiate as instantiateGeometry } from "../../../wasm/geometry/build/geometry.js";

/** 与 WASM 一致的画布宽度（px），布局根节点无 width 属性时使用。 */
export const CANVAS_WIDTH = 800;
/** 容器最小高度（px），与 DOM 模式 min-h 对齐。 */
export const MIN_CONTAINER_HEIGHT = 48;
/** snapToGuides 默认吸附阈值（px）。 */
export const DEFAULT_SNAP_THRESHOLD = 6;

export interface LayoutRect {
  id: string;
  parentId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
}

export interface RectInput {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapGuide {
  axis: "x" | "y";
  position: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  guides: SnapGuide[];
}

/** 布局输入树（ComponentNode 结构兼容：props/children 均可选）。 */
export interface LayoutTreeInput {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  children?: LayoutTreeInput[];
  /**
   * 是否容器。可选：缺省时由「是否存在 children 字段」推导
   * （children 数组存在即容器；布局计算始终使用推导值）。
   */
  container?: boolean;
}

/** 解析后的布局节点：container 由「是否存在 children 字段」判定。 */
export interface LayoutNodeParsed {
  id: string;
  type: string;
  container: boolean;
  props: Record<string, unknown>;
  children: LayoutNodeParsed[];
}

/** asc 生成的 loader 返回的 wasm 绑定（见 geometry.d.ts）。 */
export type GeometryBinding = Awaited<ReturnType<typeof instantiateGeometry>>;

// ---------------------------------------------------------------------------
// 加载 / 降级
// ---------------------------------------------------------------------------

const WASM_URL = new URL("../../../wasm/geometry/build/geometry.wasm", import.meta.url).href;

export type GeometryStatus = "loading" | "ready" | "degraded";

let bindingPromise: Promise<void> | null = null;
let wasmBinding: GeometryBinding | null = null;
let geometryStatus: GeometryStatus = "loading";

/** 抓取并实例化 wasm；任何失败返回 null（由调用方决定降级）。 */
export async function loadGeometryBinding(url: string = WASM_URL): Promise<GeometryBinding | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const bytes = await response.arrayBuffer();
    const module = await WebAssembly.compile(bytes);
    return await instantiateGeometry(module, { env: {} });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[wasm-geometry] WASM 加载失败，已降级到纯 TS 实现：", error);
    return null;
  }
}

/** 幂等初始化（单例）。调用方可 await 以在 wasm 就绪后使用；不 await 也可正常调用（TS 兜底）。 */
export function initGeometry(): Promise<void> {
  if (!bindingPromise) {
    bindingPromise = loadGeometryBinding().then((binding) => {
      wasmBinding = binding;
      geometryStatus = binding ? "ready" : "degraded";
    });
  }
  return bindingPromise;
}

export function isWasmAvailable(): boolean {
  return wasmBinding !== null;
}

export function getGeometryStatus(): GeometryStatus {
  return geometryStatus;
}

/** 测试/调试用：强制清空单例（下次 initGeometry 重新加载）。 */
export function resetGeometryForTest(): void {
  bindingPromise = null;
  wasmBinding = null;
  geometryStatus = "loading";
}

/** 测试/等价性验证用：直接注入（或清除）wasm 绑定，使类型化 API 走 wasm 路径。 */
export function setGeometryBindingForTest(binding: GeometryBinding | null): void {
  wasmBinding = binding;
  geometryStatus = binding ? "ready" : "degraded";
}

/** 从字节实例化 wasm 绑定（等价性测试/自定义加载场景复用）。 */
export async function instantiateGeometryFromBytes(bytes: BufferSource): Promise<GeometryBinding | null> {
  try {
    const module = await WebAssembly.compile(bytes);
    return await instantiateGeometry(module, { env: {} });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[wasm-geometry] WASM 实例化失败：", error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// 输入归一化
// ---------------------------------------------------------------------------

function parseLayoutInput(node: LayoutTreeInput | ComponentNode): LayoutNodeParsed {
  return {
    id: node.id,
    type: node.type,
    container: node.children !== undefined,
    props: node.props ?? {},
    children: (node.children ?? []).map(parseLayoutInput),
  };
}

/** 序列化为 wasm 输入 JSON：布局 props 扁平展开到节点对象（结构字段覆盖同名 props）。 */
function serializeTree(node: LayoutNodeParsed): string {
  return JSON.stringify(toPlainNode(node));
}

function toPlainNode(node: LayoutNodeParsed): Record<string, unknown> {
  return {
    ...node.props,
    id: node.id,
    type: node.type,
    container: node.container,
    children: node.children.map(toPlainNode),
  };
}

// ---------------------------------------------------------------------------
// 纯 TS 实现（与 assembly/index.ts 逐行对应）
// ---------------------------------------------------------------------------

function parsePx(value: string | undefined): number {
  if (!value || value.length === 0) return -1;
  const n = parseFloat(value);
  return Number.isNaN(n) ? -1 : n;
}

function maxf(a: number, b: number): number {
  return a > b ? a : b;
}

function propStr(node: LayoutNodeParsed, name: string, def = ""): string {
  const v = node.props[name];
  return typeof v === "string" ? v : def;
}

function propNum(node: LayoutNodeParsed, name: string, def = 0): number {
  const v = node.props[name];
  return typeof v === "number" ? v : def;
}

function isGrid(type: string): boolean {
  return type === "Grid";
}

function isRow(type: string, direction: string): boolean {
  if (type === "Flex") return direction === "row";
  if (type === "Container" || type === "Stack") return direction === "row";
  return false;
}

function parseGap(node: LayoutNodeParsed): number {
  const gap = parsePx(propStr(node, "gap"));
  if (gap >= 0) return gap;
  const spacing = parsePx(propStr(node, "spacing"));
  return spacing >= 0 ? spacing : 0;
}

function leafHeight(type: string, fontSize: number): number {
  if (type === "Text") return maxf(21, fontSize * 1.5);
  if (type === "Button" || type === "Input" || type === "Select" || type === "DatePicker") return 36;
  if (type === "Textarea") return 72;
  if (type === "Switch" || type === "Checkbox" || type === "Radio") return 24;
  if (type === "Slider") return 32;
  if (type === "Table") return 120;
  if (type === "Image") return 160;
  if (type === "Video") return 200;
  if (type === "AIChat") return 320;
  if (type === "Chart" || type === "BarChart" || type === "LineChart" || type === "PieChart" || type === "AreaChart") return 240;
  if (type === "MetricCard") return 80;
  return 40;
}

function leafIntrinsicW(type: string, props: Record<string, unknown>): number {
  const widthPx = parsePx(typeof props.width === "string" ? props.width : undefined);
  if (widthPx >= 0) return widthPx;
  const fontSize = typeof props.fontSize === "number" ? props.fontSize : 14;
  if (type === "Text") return maxf(48, String(props.content ?? "").length * fontSize * 0.62 + 8);
  if (type === "Button") return maxf(48, String(props.label ?? "按钮").length * fontSize * 0.62 + 24);
  if (type === "Input" || type === "Textarea" || type === "Select" || type === "DatePicker") return 220;
  if (type === "Image") return 160;
  if (type === "Video") return 240;
  return 120;
}

/** 容器在行上下文（fill=false）中的固有宽度：递归、不产出矩形。 */
function intrinsicWidthTS(node: LayoutNodeParsed): number {
  const widthPx = parsePx(propStr(node, "width"));
  if (widthPx >= 0) return widthPx;
  if (!node.container) return leafIntrinsicW(node.type, node.props);

  const padding = maxf(0, parsePx(propStr(node, "padding")));
  const gap = parseGap(node);
  if (isGrid(node.type)) {
    const columns = Math.max(1, Math.trunc(propNum(node, "columns", 3)));
    let maxChild = 0;
    for (const child of node.children) {
      maxChild = maxf(maxChild, intrinsicWidthTS(child));
    }
    return maxf(MIN_CONTAINER_HEIGHT, columns * maxChild + Math.max(0, columns - 1) * gap + 2 * padding);
  }
  const dir = propStr(node, "direction");
  if (isRow(node.type, dir)) {
    let sum = 0;
    node.children.forEach((child, i) => {
      sum += intrinsicWidthTS(child);
      if (i > 0) sum += gap;
    });
    return maxf(MIN_CONTAINER_HEIGHT, sum + 2 * padding);
  }
  let maxChild = 0;
  for (const child of node.children) {
    maxChild = maxf(maxChild, intrinsicWidthTS(child));
  }
  return maxf(MIN_CONTAINER_HEIGHT, maxChild + 2 * padding);
}

interface Size {
  width: number;
  height: number;
}

function layoutNodeTS(
  node: LayoutNodeParsed,
  x: number,
  y: number,
  availW: number,
  fill: boolean,
  depth: number,
  parentId: string,
  rects: LayoutRect[],
): Size {
  const widthPx = parsePx(propStr(node, "width"));
  let nodeW: number;
  if (widthPx >= 0) nodeW = widthPx;
  else if (depth === 0) nodeW = CANVAS_WIDTH;
  else if (fill) nodeW = availW;
  else nodeW = intrinsicWidthTS(node);

  const heightPx = parsePx(propStr(node, "height"));

  let nodeH: number;
  if (heightPx >= 0) {
    nodeH = heightPx;
  } else if (!node.container) {
    nodeH = leafHeight(node.type, propNum(node, "fontSize", 14));
  } else {
    nodeH = measureContainerTS(node, nodeW, depth);
  }

  // pre-order：先自身，后子级
  rects.push({ id: node.id, parentId, x, y, width: nodeW, height: nodeH, depth });

  if (node.container) {
    placeChildrenTS(node, x, y, nodeW, depth, rects);
  }
  return { width: nodeW, height: nodeH };
}

/** 量容器高度（子级布局写入临时 rects，丢弃）。 */
function measureContainerTS(node: LayoutNodeParsed, nodeW: number, depth: number): number {
  const padding = maxf(0, parsePx(propStr(node, "padding")));
  const gap = parseGap(node);
  const scratch: LayoutRect[] = [];
  if (isGrid(node.type)) {
    const columns = Math.max(1, Math.trunc(propNum(node, "columns", 3)));
    const innerW = maxf(0, nodeW - 2 * padding);
    const cellW = columns > 1 ? (innerW - Math.max(0, columns - 1) * gap) / columns : innerW;
    const rows = Math.ceil(node.children.length / columns);
    const rowHeights = new Array<number>(rows).fill(0);
    node.children.forEach((child, i) => {
      const s = layoutNodeTS(child, 0, 0, cellW, true, depth + 1, node.id, scratch);
      const row = Math.trunc(i / columns);
      if (s.height > rowHeights[row]) rowHeights[row] = s.height;
    });
    let h = 2 * padding;
    for (let r = 0; r < rows; r++) {
      h += rowHeights[r];
      if (r > 0) h += gap;
    }
    return maxf(MIN_CONTAINER_HEIGHT, h);
  }
  const dir = propStr(node, "direction");
  const innerW = maxf(0, nodeW - 2 * padding);
  if (isRow(node.type, dir)) {
    let maxH = 0;
    for (const child of node.children) {
      const s = layoutNodeTS(child, 0, 0, innerW, false, depth + 1, node.id, scratch);
      maxH = maxf(maxH, s.height);
    }
    return maxf(MIN_CONTAINER_HEIGHT, maxH + 2 * padding);
  }
  let cursorY = padding;
  for (const child of node.children) {
    const s = layoutNodeTS(child, 0, cursorY, innerW, true, depth + 1, node.id, scratch);
    cursorY += s.height + gap;
  }
  return maxf(MIN_CONTAINER_HEIGHT, cursorY - gap + padding);
}

/** 子级正式落位（grid 需先再量一遍行高，确定性与量高阶段一致）。 */
function placeChildrenTS(
  node: LayoutNodeParsed,
  x: number,
  y: number,
  nodeW: number,
  depth: number,
  rects: LayoutRect[],
): void {
  const padding = maxf(0, parsePx(propStr(node, "padding")));
  const gap = parseGap(node);
  if (isGrid(node.type)) {
    const columns = Math.max(1, Math.trunc(propNum(node, "columns", 3)));
    const innerW = maxf(0, nodeW - 2 * padding);
    const cellW = columns > 1 ? (innerW - Math.max(0, columns - 1) * gap) / columns : innerW;
    const rows = Math.ceil(node.children.length / columns);
    const rowHeights = new Array<number>(rows).fill(0);
    const scratch: LayoutRect[] = [];
    node.children.forEach((child, i) => {
      const s = layoutNodeTS(child, 0, 0, cellW, true, depth + 1, node.id, scratch);
      const row = Math.trunc(i / columns);
      if (s.height > rowHeights[row]) rowHeights[row] = s.height;
    });
    node.children.forEach((child, i) => {
      const col = i % columns;
      const row = Math.trunc(i / columns);
      let rowY = y + padding;
      for (let r = 0; r < row; r++) rowY += rowHeights[r] + gap;
      layoutNodeTS(child, x + padding + col * (cellW + gap), rowY, cellW, true, depth + 1, node.id, rects);
    });
    return;
  }
  const dir = propStr(node, "direction");
  const innerW = maxf(0, nodeW - 2 * padding);
  if (isRow(node.type, dir)) {
    let cursorX = x + padding;
    for (const child of node.children) {
      const s = layoutNodeTS(child, cursorX, y + padding, innerW, false, depth + 1, node.id, rects);
      cursorX += s.width + gap;
    }
  } else {
    let cursorY = y + padding;
    for (const child of node.children) {
      const s = layoutNodeTS(child, x + padding, cursorY, innerW, true, depth + 1, node.id, rects);
      cursorY += s.height + gap;
    }
  }
}

/** 纯 TS：组件树 -> 布局矩形（pre-order DFS，绝对坐标）。 */
export function computeLayoutTS(tree: LayoutTreeInput | ComponentNode): LayoutRect[] {
  const parsed = parseLayoutInput(tree);
  const rects: LayoutRect[] = [];
  layoutNodeTS(parsed, 0, 0, CANVAS_WIDTH, false, 0, "", rects);
  return rects;
}

/** 纯 TS：命中测试（rects 须 pre-order，最后命中的矩形为最上层；未命中返回 null）。 */
export function hitTestTS(x: number, y: number, rects: LayoutRect[]): string | null {
  let hit: string | null = null;
  for (const r of rects) {
    if (x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height) {
      hit = r.id;
    }
  }
  return hit;
}

/** 纯 TS：对齐吸附（边缘/中心对齐；others 须排除 moving 自身）。 */
export function snapToGuidesTS(moving: RectInput, others: RectInput[], threshold = DEFAULT_SNAP_THRESHOLD): SnapResult {
  let bestDx = threshold + 1;
  let bestDy = threshold + 1;
  let guideX = -1;
  let guideY = -1;
  const mXE = [moving.x, moving.x + moving.width / 2, moving.x + moving.width];
  const mYE = [moving.y, moving.y + moving.height / 2, moving.y + moving.height];

  for (const o of others) {
    const oXE = [o.x, o.x + o.width / 2, o.x + o.width];
    const oYE = [o.y, o.y + o.height / 2, o.y + o.height];
    for (const me of mXE) {
      for (const oe of oXE) {
        const d = oe - me;
        if (Math.abs(d) <= threshold && Math.abs(d) < Math.abs(bestDx)) {
          bestDx = d;
          guideX = oe;
        }
      }
    }
    for (const me of mYE) {
      for (const oe of oYE) {
        const d = oe - me;
        if (Math.abs(d) <= threshold && Math.abs(d) < Math.abs(bestDy)) {
          bestDy = d;
          guideY = oe;
        }
      }
    }
  }

  const guides: SnapGuide[] = [];
  if (guideX >= 0) guides.push({ axis: "x", position: guideX });
  if (guideY >= 0) guides.push({ axis: "y", position: guideY });
  return {
    dx: bestDx <= threshold ? bestDx : 0,
    dy: bestDy <= threshold ? bestDy : 0,
    guides,
  };
}

// ---------------------------------------------------------------------------
// 类型化 API（wasm 就绪走 wasm，否则 TS 兜底；结果一致）
// ---------------------------------------------------------------------------

/** 组件树 -> 布局矩形。 */
export function computeLayout(tree: LayoutTreeInput | ComponentNode): LayoutRect[] {
  const parsed = parseLayoutInput(tree);
  if (wasmBinding) {
    return JSON.parse(wasmBinding.computeLayout(serializeTree(parsed))) as LayoutRect[];
  }
  const rects: LayoutRect[] = [];
  layoutNodeTS(parsed, 0, 0, CANVAS_WIDTH, false, 0, "", rects);
  return rects;
}

/** 命中测试：返回最上层命中的节点 id，未命中返回 null。 */
export function hitTest(x: number, y: number, rects: LayoutRect[]): string | null {
  if (wasmBinding) {
    const json = JSON.stringify(
      rects.map((r) => ({
        id: r.id,
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
        depth: r.depth,
      })),
    );
    const out = JSON.parse(wasmBinding.hitTest(x, y, json)) as { id: string };
    return out.id === "" ? null : out.id;
  }
  return hitTestTS(x, y, rects);
}

/** 对齐吸附：返回吸附偏移与参考线（未命中时 dx=dy=0、guides 为空）。 */
export function snapToGuides(
  moving: RectInput,
  others: RectInput[],
  threshold = DEFAULT_SNAP_THRESHOLD,
): SnapResult {
  if (wasmBinding) {
    return JSON.parse(
      wasmBinding.snapToGuides(JSON.stringify(moving), JSON.stringify(others), threshold),
    ) as SnapResult;
  }
  return snapToGuidesTS(moving, others, threshold);
}
